import { Button, Group, Indicator, Stack, Tooltip } from "@mantine/core";
import { useCallback, useMemo } from "react";
import PopOver from "../../../components/View/PopOver";
import {
  gridSortModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import {
  IconSortAscending,
  IconSortDescending,
  IconX,
} from "@tabler/icons-react";
import { useLanguage } from "../../../stores/LanguageStore";

const TableSorter = ({ whiteButtons }) => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiContext();

  const sortingModel = useGridSelector(apiRef, gridSortModelSelector);
  const currentSort = useMemo(() => {
    if (sortingModel.length > 0) {
      return sortingModel[0];
    } else return null;
  }, [sortingModel]);

  const handleSortChange = useCallback(
    (newModel, closePopover) => {
      if (!newModel) {
        return apiRef.current.setSortModel([]);
      }
      let newSortingFilter = {
        field: newModel.field,
        sort: newModel.direction,
      };

      apiRef.current.setSortModel([newSortingFilter]);

      closePopover();
    },
    [apiRef]
  );

  return (
    <Group gap={0} mt={2} ml={whiteButtons ? 16 : "auto"}>
      <PopOver
        opener={(openFunction, ref) => {
          return (
            <Group gap={0}>
              {whiteButtons ? (
                <Tooltip label={lang.table.toolbar.filter.main_button_tooltip}>
                  <Button
                    ref={ref}
                    onClick={openFunction}
                    radius={"md"}
                    variant={"subtle"}
                    className="test"
                    style={{
                      borderColor: "var(--mantine-color-primary-0)",
                      background: "#fff",
                      borderTopRightRadius: currentSort ? 0 : 12,
                      borderBottomRightRadius: currentSort ? 0 : 12,
                    }}
                  >
                    <Indicator
                      color="red"
                      offset={2}
                      disabled={!currentSort}
                      styles={{
                        indicator: {
                          padding: "4px 4px",
                        },
                      }}
                      size={12}
                    >
                      {currentSort?.sort === "desc" ? (
                        <IconSortDescending size={24} />
                      ) : (
                        <IconSortAscending size={24} />
                      )}
                    </Indicator>
                    <span style={{ marginLeft: 6 }}>
                      {lang.table.toolbar.sort}
                    </span>
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip label={lang.table.toolbar.sort}>
                  <Indicator
                    color="red"
                    offset={2}
                    disabled={!currentSort}
                    styles={{
                      indicator: {
                        padding: "4px 4px",
                      },
                    }}
                    size={12}
                  >
                    <Button
                      ref={ref}
                      onClick={openFunction}
                      variant={currentSort ? "light" : "subtle"}
                      size={"sm"}
                      miw={0}
                      w={38}
                      p={0}
                      radius={"md"}
                      style={{
                        borderTopRightRadius: currentSort ? 0 : 12,
                        borderBottomRightRadius: currentSort ? 0 : 12,
                      }}
                    >
                      {currentSort?.sort === "desc" ? (
                        <IconSortDescending size={24} />
                      ) : (
                        <IconSortAscending size={24} />
                      )}
                    </Button>
                  </Indicator>
                </Tooltip>
              )}

              {currentSort && (
                <Tooltip label={lang.table.toolbar.cancel_sort}>
                  <Button
                    variant="light"
                    color="red"
                    px={4}
                    radius={"md"}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      borderColor: "var(--mantine-color-primary-0)",
                      borderLeft: 0,
                    }}
                    onClick={() => handleSortChange(null)}
                  >
                    <IconX size={18} />
                  </Button>
                </Tooltip>
              )}
            </Group>
          );
        }}
        popoverChildren={(closePopover) => (
          <Stack gap={"sm"}>
            <Button
              leftSection={
                currentSort?.field === "name" ? (
                  currentSort?.sort === "asc" ? (
                    <IconSortAscending />
                  ) : (
                    <IconSortDescending />
                  )
                ) : null
              }
              onClick={() =>
                handleSortChange(
                  {
                    field: "name",
                    direction: currentSort?.sort === "asc" ? "desc" : "asc",
                  },
                  closePopover
                )
              }
              variant="light"
            >
              {lang.table.toolbar.sort_alpha}
            </Button>

            <Button
              leftSection={
                currentSort?.field === "created_on" ? (
                  currentSort?.sort === "asc" ? (
                    <IconSortAscending />
                  ) : (
                    <IconSortDescending />
                  )
                ) : null
              }
              onClick={() =>
                handleSortChange(
                  {
                    field: "created_on",
                    direction: currentSort?.sort === "asc" ? "desc" : "asc",
                  },
                  closePopover
                )
              }
              variant="light"
            >
              {lang.table.toolbar.sort_date}
            </Button>
          </Stack>
        )}
        paperSx={{
          borderRadius: 3,
        }}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
      />
    </Group>
  );
};

export default TableSorter;
